"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useAuth } from "@/hooks/useAuth"
import { getCollections, type BiblioCollection } from "@/services/biblioService"
import {
  getCollectionScope,
  saveCollectionScope,
} from "@/services/collectionScopeService"
import { useInvocationContext } from "./InvocationContext"

/**
 * A "collection" is a biblio collection: a named set of bibliographic source
 * IDs.
 */

/** Shape of the context exposed to consumers of the collection state. */
interface CollectionContextValue {
  /** All collections (biblio collections) available in the current environment. */
  collections: BiblioCollection[]
  /**
   * The numeric id of the currently selected collection, or null when no
   * collection is selected (the default). Only one may be selected at a time.
   */
  selectedCollectionId: number | null
  /**
   * The currently selected collection record, or null when none is selected.
   * Derived from {@link selectedCollectionId} for consumer convenience.
   */
  selectedCollection: BiblioCollection | null
  /**
   * Select a collection by id, or pass null to clear the selection. Selecting
   * a different id replaces any prior selection (single-select semantics).
   *
   * @param collectionId - The collection id to select, or null to clear.
   */
  selectCollection: (collectionId: number | null) => void
  /**
   * Re-fetch the collections list from the backend. Call this after a mutation
   * that changes a collection's membership (e.g. adding a source to one) so the
   * panel reflects the new source counts immediately.
   *
   * @returns A promise that resolves once the refresh completes.
   */
  refreshCollections: () => Promise<void>
  /** True while the initial fetch (or a manual refresh) of collections is in flight. */
  loading: boolean
  /**
   * The error message from the most recent failed fetch, or null when the last
   * fetch succeeded. Surfaced so the panel can fail loudly rather than showing
   * an empty list that looks like "no collections exist".
   */
  error: string | null
}

// Inert default so consumers rendered outside a provider (e.g. non-deep-lit
// chats) do not crash and simply see "no collections, nothing selected".
const DEFAULT_VALUE: CollectionContextValue = {
  collections: [],
  selectedCollectionId: null,
  selectedCollection: null,
  selectCollection: () => {},
  refreshCollections: async () => {},
  loading: false,
  error: null,
}

const CollectionContext = createContext<CollectionContextValue>(DEFAULT_VALUE)

/** Props for the {@link CollectionProvider}. */
interface CollectionProviderProps {
  /**
   * The active chat session UUID. The selected collection is persisted per
   * session, so the provider restores this session's saved selection when the
   * id changes and writes selections back keyed by it.
   */
  sessionId: string
  /**
   * Whether the active session's backend row exists yet. A selection can only
   * be persisted once it does (the scope row's TTL is synced to the session).
   * When false, a selection change updates in-memory state only; it is written
   * later by the first-persist listener once the session row is created. This
   * gate avoids firing a PUT we know would be rejected before the session
   * exists.
   */
  isSessionPersisted: boolean
  children: ReactNode
}

/**
 * Provider that fetches the available collections (biblio collections) once
 * and shares them, plus the single-or-none selection state, with every
 * descendant — the right-hand collection panel and the chat interface.
 *
 * @param props - Provider props.
 * @returns React element wrapping children with collection state.
 */
export function CollectionProvider({
  sessionId,
  isSessionPersisted,
  children,
}: CollectionProviderProps): React.JSX.Element {
  const { getAccessToken } = useAuth()
  const [collections, setCollections] = useState<BiblioCollection[]>([])
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Monotonic fetch id: each fetch captures the current value and discards its
  // result if a newer fetch has started, preventing stale overwrites.
  const fetchIdRef = useRef<number>(0)

  /**
   * Fetch all collections from the backend. Used by both the initial load
   * effect and the public refresh. Guards against stale responses via the
   * fetchIdRef counter and fails loudly by surfacing an error message.
   *
   * @returns A Promise that resolves once the fetch completes and state updates.
   */
  const fetchCollections = useCallback(async (): Promise<void> => {
    const thisFetchId: number = ++fetchIdRef.current
    setLoading(true)
    setError(null)
    try {
      const token: string | null = await getAccessToken()
      if (!token) {
        throw new Error("Authentication required to load collections.")
      }

      const response = await getCollections(token)

      if (fetchIdRef.current !== thisFetchId) {
        return
      }

      // Sort alphabetically by name for a stable, scannable list. Records with
      // no name are pushed to the bottom rather than crashing localeCompare on an undefined value.
      const sorted: BiblioCollection[] = [...response.items].sort((a, b) => {
        const nameA: string = a.collectionName ?? ""
        const nameB: string = b.collectionName ?? ""
        if (!nameA && !nameB) {
          return 0
        }
        if (!nameA) {
          return 1
        }
        if (!nameB) {
          return -1
        }
        return nameA.localeCompare(nameB)
      })
      setCollections(sorted)
    } catch (err) {
      if (fetchIdRef.current !== thisFetchId) {
        return
      }
      const message: string =
        err instanceof Error ? err.message : "Failed to load collections."
      console.error("Failed to fetch collections:", err)
      setError(message)
      setCollections([])
    } finally {
      if (fetchIdRef.current === thisFetchId) {
        setLoading(false)
      }
    }
  }, [getAccessToken])

  /** Initial load: fetch the collections once when the provider mounts. */
  useEffect(() => {
    void fetchCollections()
  }, [fetchCollections])

  // Guards against the restore effect's setSelectedCollectionId being treated
  // as a user selection and written straight back to the backend. When true,
  // selection changes are applied to local state only (no persistence).
  const restoringRef = useRef<boolean>(false)

  // Latest sessionId, read by callbacks that must target the current session
  // without re-creating on every session switch.
  const sessionIdRef = useRef<string>(sessionId)
  sessionIdRef.current = sessionId

  // Latest persisted flag, read lazily by persistSelection so it always sees
  // the current session's state without re-creating the callback.
  const isSessionPersistedRef = useRef<boolean>(isSessionPersisted)
  isSessionPersistedRef.current = isSessionPersisted

  /**
   * Persist a selection for the active session to the backend. Best-effort.
   *
   * @param collectionId - The collection id to persist, or null for "None".
   * @param force - When true, skip the persisted gate. The first-persist
   *   listener passes this: it fires exactly at the not-persisted → persisted
   *   transition, before the `isSessionPersisted` prop has re-rendered, so the
   *   session row provably exists even though our local flag hasn't caught up.
   * @returns A promise that resolves once the write attempt completes.
   */
  const persistSelection = useCallback(
    async (collectionId: number | null, force: boolean = false): Promise<void> => {
      const activeSessionId: string = sessionIdRef.current
      if (!activeSessionId) {
        return
      }
      // Only write once the session exists.
      if (!force && !isSessionPersistedRef.current) {
        return
      }
      try {
        const token: string | null = await getAccessToken()
        if (!token) {
          return
        }
        await saveCollectionScope(activeSessionId, collectionId, token)
      } catch (err) {
        // Non-fatal — the in-memory selection still works for this session;
        console.warn("Failed to persist collection scope:", err)
      }
    },
    [getAccessToken]
  )

  /**
   * Select a collection by id, or clear the selection with null. Updates local
   * state and, when the change is user-driven (not a restore), persists it for
   * the active session so it survives reloads and session switches.
   *
   * @param collectionId - The collection id to select, or null to clear.
   */
  const selectCollection = useCallback(
    (collectionId: number | null): void => {
      // Reject selecting an id that isn't in the loaded list (except null,
      // which always clears).
      const isValid: boolean =
        collectionId === null || collections.some((c) => c.id === collectionId)
      if (!isValid) {
        return
      }

      setSelectedCollectionId(collectionId)

      // Only persist genuine user selections. Restores apply state directly
      // via the restore effect with restoringRef set, so they don't echo back.
      if (!restoringRef.current) {
        void persistSelection(collectionId)
      }
    },
    [collections, persistSelection]
  )

  // Restore the saved selection whenever the active session changes
  useEffect(() => {
    let cancelled = false

    async function restoreScope(): Promise<void> {
      if (!sessionId) {
        return
      }
      try {
        const token: string | null = await getAccessToken()
        if (!token || cancelled) {
          return
        }
        const scope = await getCollectionScope(sessionId, token)
        if (cancelled) {
          return
        }
        restoringRef.current = true
        try {
          setSelectedCollectionId(scope.collectionId)
        } finally {
          restoringRef.current = false
        }
      } catch (err) {
        // A failed restore should not break the chat — just leave the
        // selection as-is and log for triage.
        console.warn("Failed to restore collection scope:", err)
      }
    }

    void restoreScope()

    return () => {
      cancelled = true
    }
  }, [sessionId, getAccessToken])

  /**
   * Persist the current selection for the active session at the moment the
   * session is first persisted.
   */
  const selectedIdRef = useRef<number | null>(selectedCollectionId)
  selectedIdRef.current = selectedCollectionId

  const persistScope = useCallback(async (): Promise<void> => {
    await persistSelection(selectedIdRef.current, /* force */ true)
  }, [persistSelection])

  // If the selected collection disappears after a refresh, clear the stale
  // selection so we never reference a collection that no longer exists. This is
  // a local correction only (restoringRef guards against a spurious write).
  useEffect(() => {
    if (
      selectedCollectionId !== null &&
      collections.length > 0 &&
      !collections.some((c) => c.id === selectedCollectionId)
    ) {
      restoringRef.current = true
      try {
        setSelectedCollectionId(null)
      } finally {
        restoringRef.current = false
      }
    }
  }, [collections, selectedCollectionId])

  const selectedCollection: BiblioCollection | null = useMemo(
    () => collections.find((c) => c.id === selectedCollectionId) ?? null,
    [collections, selectedCollectionId]
  )

  // Contribute the selected collection's source IDs to the generic
  // invocation-context registry, so a message send carries `sourceIds` to the
  // runtime without the generic chat send path knowing about collections.
  // Registered once; the contributor reads the latest selection via a ref so it
  // always reflects the current choice without re-registering on every change.
  const { register, registerPersistListener } = useInvocationContext()
  const selectedCollectionRef = useRef<BiblioCollection | null>(selectedCollection)
  selectedCollectionRef.current = selectedCollection

  useEffect(() => {
    const unregister = register((): Record<string, unknown> => {
      const collection = selectedCollectionRef.current
      const sourceIds = collection?.sourceIds
      if (!sourceIds || sourceIds.length === 0) {
        return {}
      }
      // collectionName is framing only (lets the agent name the scope); the
      // sourceIds are what actually enforce it.
      return { sourceIds, collectionName: collection?.collectionName }
    })
    return unregister
  }, [register])

  // Persist the current selection once the session row is first persisted, via
  // the generic persist-listener seam.
  useEffect(() => {
    const unregister = registerPersistListener((): void => {
      void persistScope()
    })
    return unregister
  }, [registerPersistListener, persistScope])

  const value: CollectionContextValue = useMemo(
    () => ({
      collections,
      selectedCollectionId,
      selectedCollection,
      selectCollection,
      refreshCollections: fetchCollections,
      loading,
      error,
    }),
    [
      collections,
      selectedCollectionId,
      selectedCollection,
      selectCollection,
      fetchCollections,
      loading,
      error,
    ]
  )

  return (
    <CollectionContext.Provider value={value}>{children}</CollectionContext.Provider>
  )
}

/**
 * Hook for consumers (the collection panel and the chat interface) to read
 * the shared collection state and selection.
 *
 * @returns The current {@link CollectionContextValue}.
 */
export function useCollection(): CollectionContextValue {
  return useContext(CollectionContext)
}
